import { database } from './config';
import { ref, get, set, runTransaction, push, onValue, off, remove } from 'firebase/database';

// Sophisticated ELO Configuration
const SOPHISTICATED_ELO_CONFIG = {
  MIN_RATING: 200,
  MAX_RATING: 1000,
  DEFAULT_RATING: 400,
  BASE_DIVISOR: 300,
  K_OPPONENT: 3,
  MEAN_REVERSION_ALPHA: 0.001  // Very light mean reversion
};

console.log('🚨🚨🚨 ELO SERVICE LOADED AT', new Date().toISOString(), '- USING SOPHISTICATED ELO SYSTEM 🚨🚨🚨');

/**
 * Convert placement to score value (for expected vs actual performance)
 * @param {number} place - 1st, 2nd, 3rd, or 4th
 * @returns {number} Score between 0 and 1
 */
function placementToScore(place) {
  return (4 - place) / 3;
}

/**
 * Calculate expected performance against opponents
 * @param {number} playerRating - Player's current rating
 * @param {number} avgOpponentRating - Average rating of 3 opponents
 * @returns {number} Expected performance (0-1)
 */
function calculateExpectedPerformance(playerRating, avgOpponentRating) {
  const ratingDiff = avgOpponentRating - playerRating;
  return 1 / (1 + Math.pow(10, ratingDiff / SOPHISTICATED_ELO_CONFIG.BASE_DIVISOR));
}

/**
 * Calculate opponent strength bonus (performance vs expectation)
 * @param {number} place - Player's placement (1-4)
 * @param {number} playerRating - Player's current rating
 * @param {number} avgOpponentRating - Average rating of 3 opponents
 * @returns {number} Performance-based rating change
 */
function calculatePerformanceBonus(place, playerRating, avgOpponentRating) {
  const actualScore = placementToScore(place);
  const expectedScore = calculateExpectedPerformance(playerRating, avgOpponentRating);
  return SOPHISTICATED_ELO_CONFIG.K_OPPONENT * (actualScore - expectedScore);
}

/**
 * Calculate mean reversion component (very light)
 * @param {number} rating - Current rating
 * @returns {number} Mean reversion delta
 */
function calculateMeanReversion(rating) {
  return SOPHISTICATED_ELO_CONFIG.MEAN_REVERSION_ALPHA * (SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING - rating);
}

/**
 * Calculate one-sided damping (only dampens high ELO players)
 * @param {number} rating - Current rating
 * @returns {number} Damping factor (0.5 to 1.0)
 */
function calculateOneSidedDamping(rating) {
  if (rating <= SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING) {
    return 1.0; // No damping for players at or below default
  }
  
  const excessRating = rating - SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING;
  const maxExcess = SOPHISTICATED_ELO_CONFIG.MAX_RATING - SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING;
  const dampingFactor = 1 - 0.5 * (excessRating / maxExcess);
  return Math.max(0.5, Math.min(1.0, dampingFactor));
}

/**
 * Apply zero-sum constraint to ensure deltas sum to zero
 * @param {number[]} deltas - Array of rating deltas
 * @returns {number[]} Adjusted deltas that sum to zero
 */
function applyZeroSumConstraint(deltas) {
  const sum = deltas.reduce((total, delta) => total + delta, 0);
  const adjustment = sum / deltas.length;
  return deltas.map(delta => delta - adjustment);
}

/**
 * Clamp rating to valid range
 * @param {number} rating - Rating to clamp
 * @returns {number} Clamped rating
 */
function clampRating(rating) {
  return Math.max(SOPHISTICATED_ELO_CONFIG.MIN_RATING, Math.min(SOPHISTICATED_ELO_CONFIG.MAX_RATING, rating));
}

function resolveHistoryTimestamp(dateValue, fallback = Date.now()) {
  const parsed = Date.parse(dateValue || '');
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * Fetch current Elo rating for a player
 * @param {string} playerId - Player identifier
 * @returns {Promise<number>} Current Elo rating
 */
async function fetchPlayerElo(playerId, historyTimestamp = Date.now()) {
  try {
    console.log(`\n🔍 ========== FETCHING ELO FOR PLAYER: "${playerId}" ==========`);
    
    // First check ELO history for this exact player name
    const historyRef = ref(database, `ELO_history/${playerId}`);
    const snapshot = await get(historyRef);
    
    if (snapshot.exists()) {
      const historyData = snapshot.val();
      
      // Find the most recent entry by timestamp
      const entries = Object.values(historyData);
      if (entries.length > 0) {
        const mostRecent = entries.reduce((latest, entry) => {
          return entry.at > latest.at ? entry : latest;
        });
        
        console.log(`✅ FOUND IN HISTORY: ${playerId} has ${mostRecent.value} ELO (${entries.length} history entries)`);
        return mostRecent.value;
      }
    }
    
    console.log(`📊 No history for "${playerId}", searching ELO database...`);
    
    // Get the entire ELO node to search comprehensively
    const allEloRef = ref(database, 'ELO');
    const allEloSnapshot = await get(allEloRef);
    
    if (!allEloSnapshot.exists()) {
      console.log(`⚠️ ELO database is empty!`);
      console.log(`🆕 NEW PLAYER "${playerId}" starting at ${SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING}`);
      await recordEloHistory(playerId, SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING, historyTimestamp);
      return SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING;
    }
    
    const allElo = allEloSnapshot.val();
    console.log(`📚 Searching ${Object.keys(allElo).length} entries in ELO database...`);
    
    // Strategy 1: Direct exact match
    if (allElo[playerId] !== undefined && allElo[playerId] !== null) {
      const existingElo = allElo[playerId];
      console.log(`✅ EXACT MATCH FOUND: "${playerId}" = ${existingElo} ELO`);
      console.log(`📝 Creating first history entry at ${existingElo}`);
      await recordEloHistory(playerId, existingElo, historyTimestamp);
      return existingElo;
    }
    
    // Strategy 2: Case-insensitive exact match
    const playerIdLower = playerId.toLowerCase().trim();
    const playerIdUpper = playerId.toUpperCase().trim();
    
    for (const [key, value] of Object.entries(allElo)) {
      if (typeof value !== 'number') continue;
      
      const keyLower = key.toLowerCase().trim();
      if (keyLower === playerIdLower) {
        console.log(`✅ CASE-INSENSITIVE MATCH: "${key}" matches "${playerId}" = ${value} ELO`);
        console.log(`📝 Creating first history entry at ${value}`);
        await recordEloHistory(playerId, value, historyTimestamp);
        return value;
      }
    }
    
    // Strategy 3: Remove special characters and spaces for matching
    const normalizedPlayerId = playerId.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    
    for (const [key, value] of Object.entries(allElo)) {
      if (typeof value !== 'number') continue;
      
      const normalizedKey = key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      if (normalizedKey === normalizedPlayerId && normalizedPlayerId.length > 2) {
        console.log(`✅ NORMALIZED MATCH: "${key}" matches "${playerId}" (both normalize to "${normalizedPlayerId}") = ${value} ELO`);
        console.log(`📝 Creating first history entry at ${value}`);
        await recordEloHistory(playerId, value, historyTimestamp);
        return value;
      }
    }
    
    // Strategy 4: Partial match (only if player name is meaningful length)
    if (playerId.length >= 3) {
      // Check if any key contains the player name or vice versa
      for (const [key, value] of Object.entries(allElo)) {
        if (typeof value !== 'number') continue;
        
        const keyLower = key.toLowerCase().trim();
        
        // Only do partial matching if there's a strong match
        if ((keyLower.includes(playerIdLower) && playerIdLower.length >= 4) || 
            (playerIdLower.includes(keyLower) && keyLower.length >= 4)) {
          console.log(`⚠️ PARTIAL MATCH FOUND: "${key}" partial matches "${playerId}" = ${value} ELO`);
          console.log(`📝 Creating first history entry at ${value}`);
          await recordEloHistory(playerId, value, historyTimestamp);
          return value;
        }
      }
    }
    
    // Log all keys in ELO database for debugging
    console.log(`❌ NO MATCH FOUND for "${playerId}"`);
    console.log(`📋 Available players in ELO database:`, Object.keys(allElo).sort());
    
    // Player truly doesn't exist in ELO database
    console.log(`🆕 CONFIRMED NEW PLAYER "${playerId}" - starting at ${SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING}`);
    await recordEloHistory(playerId, SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING, historyTimestamp);
    return SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING;
    
  } catch (error) {
    console.error(`❌ ERROR fetching ELO for player "${playerId}":`, error);
    console.log(`⚠️ FALLBACK: Using default ${SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING} due to error`);
    return SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING;
  }
}

/**
 * Record ELO history for a player
 * @param {string} playerId - Player identifier
 * @param {number} newRating - New ELO rating to record
 * @returns {Promise<void>}
 */
async function recordEloHistory(playerId, newRating, historyTimestamp = Date.now()) {
  try {
    const historyRef = ref(database, `ELO_history/${playerId}`);
    const historyEntry = {
      value: newRating,
      at: historyTimestamp
    };
    await push(historyRef, historyEntry);
    console.log(`📝 Recorded ELO history for ${playerId}: ${newRating}`);
  } catch (error) {
    console.error(`❌ Error recording ELO history for ${playerId}:`, error);
  }
}

/**
 * Update Elo ratings for all players in a race using sophisticated algorithm
 * @param {Object} raceData - Object containing player finishes { playerId: { finish: position }, ... }
 * @returns {Promise<Object>} Object with new ratings { playerId: newRating, ... }
 */
export async function updateEloForRace(raceData, raceTimestamp = Date.now()) {
  console.log('🏁 UPDATE ELO FOR RACE - SOPHISTICATED SYSTEM:', raceData);
  const playerIds = Object.keys(raceData);
  const numPlayers = playerIds.length;
  
  if (numPlayers !== 4) {
    console.warn('Sophisticated ELO system expects exactly 4 players per race');
    return {};
  }
  
  console.log(`📊 Processing 4-player race:`, playerIds);
  
  // Fetch current ratings for all players
  const players = [];
  const ratings = [];
  const placements = [];
  
  for (const playerId of playerIds) {
    const rating = await fetchPlayerElo(playerId, raceTimestamp);
    players.push(playerId);
    ratings.push(rating);
    placements.push(raceData[playerId].finish);
    console.log(`📊 ${playerId}: ${rating} ELO, finished ${raceData[playerId].finish}`);
  }
  
  // Validate placements are 1, 2, 3, 4
  const sortedPlacements = [...placements].sort();
  if (JSON.stringify(sortedPlacements) !== JSON.stringify([1, 2, 3, 4])) {
    console.error('Invalid placements - must be exactly [1, 2, 3, 4]:', placements);
    return {};
  }
  
  console.log('📊 Starting sophisticated ELO calculation...');
  
  // Calculate performance-based deltas for each player
  const performanceDeltas = [];
  
  for (let i = 0; i < 4; i++) {
    const playerRating = ratings[i];
    const playerPlace = placements[i];
    
    // Calculate average opponent rating (exclude current player)
    const opponentRatings = ratings.filter((_, index) => index !== i);
    const avgOpponentRating = opponentRatings.reduce((sum, r) => sum + r, 0) / 3;
    
    // Calculate performance-based delta (no fixed placement bonus)
    const performanceBonus = calculatePerformanceBonus(playerPlace, playerRating, avgOpponentRating);
    
    // Apply one-sided damping
    const dampingFactor = calculateOneSidedDamping(playerRating);
    const dampedPerformance = performanceBonus * dampingFactor;
    
    // Add mean reversion (very light)
    const meanReversion = calculateMeanReversion(playerRating);
    
    const totalDelta = dampedPerformance + meanReversion;
    performanceDeltas.push(totalDelta);
    
    console.log(`📊 ${players[i]}: performance=${performanceBonus.toFixed(2)}, damped=${dampedPerformance.toFixed(2)}, reversion=${meanReversion.toFixed(3)}, total=${totalDelta.toFixed(2)}`);
  }
  
  // Apply zero-sum constraint
  const adjustedDeltas = applyZeroSumConstraint(performanceDeltas);
  
  console.log('📊 Zero-sum adjustment applied');
  
  // Calculate new ratings and prepare for Firebase update
  const newRatings = {};
  
  for (let i = 0; i < 4; i++) {
    const newRating = clampRating(ratings[i] + adjustedDeltas[i]);
    newRatings[players[i]] = Math.round(newRating);
    
    console.log(`📊 ${players[i]}: ${ratings[i]} -> ${newRatings[players[i]]} (Δ${adjustedDeltas[i].toFixed(2)})`);
  }
  
  // Update ratings in Firebase (history only - no more main ELO storage)
  console.log('📤 Updating ELO history with sophisticated ratings:', newRatings);
  await Promise.all(
    players.map(async (playerId) => {
      // Only record to ELO history - this becomes the source of truth
      await recordEloHistory(playerId, newRatings[playerId], raceTimestamp + 1);
      console.log(`✅ Updated ${playerId} to ${newRatings[playerId]} (history-based)`);
    })
  );
  
  console.log('✅ Sophisticated ELO ratings updated successfully');
  return newRatings;
}

/**
 * Process a match from act-history and update Elo ratings
 * @param {string} cupId - The cup identifier
 * @param {string} matchId - The match identifier
 * @param {Object} matchData - The match data containing players and finishes
 * @returns {Promise<void>}
 */
export async function processMatchForElo(cupId, matchId, matchData) {
  console.log(`📊 Processing match ${cupId}/${matchId} for Elo updates`);
  
  if (!matchData.players) {
    console.warn('Match data missing players field');
    return;
  }
  
  try {
    await updateEloForRace(matchData.players);
    console.log(`✅ Elo updated for match ${cupId}/${matchId}`);
  } catch (error) {
    console.error(`❌ Error updating Elo for match ${cupId}/${matchId}:`, error);
    throw error;
  }
}

/**
 * Helper function to format player data for Elo calculation
 * This converts from the ACT scorecard format to the expected format
 * @param {Object} actData - The ACT data saved to Firebase
 * @returns {Object} Formatted player data for each race
 */
export function formatActDataForElo(actData) {
  console.log('🔍 formatActDataForElo - Input data:', JSON.stringify(actData, null, 2));
  
  const raceResults = [];
  
  // Process each cup
  Object.keys(actData.races || {}).forEach(cup => {
    console.log(`🏁 Processing cup: ${cup}`);
    const cupRaces = actData.races[cup];
    
    // Process each race in the cup (1-4 for 8-person, 1-6 for 12-person)
    Object.keys(cupRaces || {}).forEach(raceNum => {
      console.log(`  🏎️ Processing race ${raceNum} in ${cup}`);
      const race = cupRaces[raceNum];
      const raceData = {};
      
      // Process each team's result
      Object.keys(race || {}).forEach(team => {
        const teamData = race[team];
        console.log(`    Team ${team}:`, teamData);
        if (teamData.racer && teamData.place && teamData.place !== 'X') {
          // Use racer name directly as player ID
          const playerId = teamData.racer;
          raceData[playerId] = { finish: teamData.place };
          console.log(`    ✅ Added player ${playerId} with finish ${teamData.place}`);
        } else {
          console.log(`    ❌ Skipped team ${team} - racer: ${teamData.racer}, place: ${teamData.place}`);
        }
      });
      
      console.log(`  Race ${raceNum} final data:`, raceData);
      
      // Only add races with valid player data
      if (Object.keys(raceData).length >= 2) {
        raceResults.push({
          cup,
          raceNum,
          players: raceData
        });
        console.log(`  ✅ Added race ${cup}-${raceNum} with ${Object.keys(raceData).length} players`);
      } else {
        console.log(`  ❌ Skipped race ${cup}-${raceNum} - only ${Object.keys(raceData).length} players`);
      }
    });
  });
  
  console.log(`🏁 formatActDataForElo - Final result: ${raceResults.length} races`, raceResults);
  return raceResults;
}

/**
 * Process an entire ACT entry and update Elo for all races
 * @param {Object} actData - The ACT data that was saved
 * @returns {Promise<void>}
 */
export async function processActForElo(actData) {
  console.log('📊 Processing ACT entry for Elo updates');
  
  const raceResults = formatActDataForElo(actData);
  
  if (raceResults.length === 0) {
    console.warn('No valid races found in ACT data for Elo processing');
    return;
  }
  
  // Process each race sequentially to maintain proper Elo progression
  const actTimestamp = resolveHistoryTimestamp(actData?.date, actData?.timestamp || Date.now());

  for (const [raceIndex, race] of raceResults.entries()) {
    console.log(`Processing ${race.cup} race ${race.raceNum}`);
    await updateEloForRace(race.players, actTimestamp + raceIndex * 10);
  }
  
  console.log(`✅ Processed ${raceResults.length} races for Elo updates`);
}

/**
 * Fetch ELO history for a specific player from Firebase
 * @param {string} playerId - Player identifier
 * @returns {Promise<Array>} Array of ELO history entries
 */
export async function fetchPlayerEloHistory(playerId) {
  try {
    console.log(`📊 Fetching ELO history for ${playerId}`);
    const historyRef = ref(database, `ELO_history/${playerId}`);
    const snapshot = await get(historyRef);
    
    if (snapshot.exists()) {
      const historyData = snapshot.val();
      // Convert Firebase object to array and sort by timestamp
      const historyArray = Object.entries(historyData).map(([key, entry]) => ({
        id: key,
        value: entry.value,
        at: entry.at
      })).sort((a, b) => a.at - b.at);
      
      console.log(`✅ Found ${historyArray.length} ELO history entries for ${playerId}`);
      return historyArray;
    }
    
    console.log(`❌ No ELO history found for ${playerId}`);
    return [];
  } catch (error) {
    console.error(`❌ Error fetching ELO history for ${playerId}:`, error);
    return [];
  }
}

/**
 * Subscribe to ELO history updates for a player
 * @param {string} playerId - Player identifier
 * @param {Function} callback - Callback function to handle updates
 * @returns {Function} Unsubscribe function
 */
export function subscribeToPlayerEloHistory(playerId, callback) {
  const historyRef = ref(database, `ELO_history/${playerId}`);
  
  const listener = onValue(historyRef, (snapshot) => {
    if (snapshot.exists()) {
      const historyData = snapshot.val();
      const historyArray = Object.entries(historyData).map(([key, entry]) => ({
        id: key,
        value: entry.value,
        at: entry.at
      })).sort((a, b) => a.at - b.at);
      callback(historyArray);
    } else {
      callback([]);
    }
  });
  
  // Return unsubscribe function
  return () => off(historyRef, 'value', listener);
}

/**
 * Fetch ELO histories for multiple players
 * @param {Array<string>} playerIds - Array of player identifiers
 * @returns {Promise<Object>} Object with player names as keys and history arrays as values
 */
export async function fetchMultipleEloHistories(playerIds) {
  try {
    const histories = {};
    
    await Promise.all(
      playerIds.map(async (playerId) => {
        const history = await fetchPlayerEloHistory(playerId);
        if (history.length > 0) {
          histories[playerId] = history;
        }
      })
    );
    
    console.log(`✅ Fetched ELO histories for ${Object.keys(histories).length} players`);
    return histories;
  } catch (error) {
    console.error('❌ Error fetching multiple ELO histories:', error);
    return {};
  }
}

/**
 * Consolidate ELO history from old player name to new player name
 * @param {string} oldName - Old player name
 * @param {string} newName - New player name
 * @returns {Promise<boolean>} True if consolidation was successful
 */
export async function consolidatePlayerEloHistory(oldName, newName) {
  try {
    console.log(`🔄 Consolidating ELO history from "${oldName}" to "${newName}"`);

    // Get existing ELO history for old name
    const oldHistoryRef = ref(database, `ELO_history/${oldName}`);
    const oldSnapshot = await get(oldHistoryRef);

    if (!oldSnapshot.exists()) {
      console.log(`📊 No ELO history found for old name "${oldName}"`);
      return false;
    }

    const oldHistoryData = oldSnapshot.val();
    console.log(`📊 Found ${Object.keys(oldHistoryData).length} ELO history entries for "${oldName}"`);

    // Get existing ELO history for new name (if any)
    const newHistoryRef = ref(database, `ELO_history/${newName}`);
    const newSnapshot = await get(newHistoryRef);

    let consolidatedHistory = {};

    if (newSnapshot.exists()) {
      console.log(`📊 Found existing ELO history for "${newName}", merging...`);
      consolidatedHistory = newSnapshot.val();
    }

    // Add all old history entries to new name
    Object.entries(oldHistoryData).forEach(([key, entry]) => {
      // Use a new key to avoid conflicts
      const newKey = `${key}_migrated_${Date.now()}`;
      consolidatedHistory[newKey] = {
        ...entry,
        migratedFrom: oldName,
        migratedAt: Date.now()
      };
    });

    // Save consolidated history under new name
    await set(newHistoryRef, consolidatedHistory);
    console.log(`✅ Consolidated ${Object.keys(oldHistoryData).length} ELO entries under "${newName}"`);

    // Remove old ELO history (optional - could be kept for audit trail)
    await remove(oldHistoryRef);
    console.log(`🗑️ Removed old ELO history for "${oldName}"`);

    return true;
  } catch (error) {
    console.error(`❌ Error consolidating ELO history from "${oldName}" to "${newName}":`, error);
    return false;
  }
}

/**
 * Get ELO statistics for a player (current, high, low)
 * @param {string} playerId - Player identifier
 * @returns {Promise<Object>} Object with current, high, and low ELO values
 */
export async function getPlayerEloStats(playerId) {
  try {
    // Get ELO history (single source of truth)
    const history = await fetchPlayerEloHistory(playerId);
    const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();
    const cutoff = now - THIRTY_DAYS_MS;
    const validHistory = history
      .map(entry => ({
        ...entry,
        at: Number(entry.at),
        value: Number(entry.value)
      }))
      .filter(entry => Number.isFinite(entry.at) && Number.isFinite(entry.value))
      .sort((a, b) => a.at - b.at);
    let change30d = null;
    let change30dPartialWindow = false;

    if (validHistory.length > 0) {
      const currentEntry = validHistory[validHistory.length - 1];
      let baselineEntry = null;

      for (let i = validHistory.length - 1; i >= 0; i--) {
        if (validHistory[i].at <= cutoff) {
          baselineEntry = validHistory[i];
          break;
        }
      }

      if (!baselineEntry) {
        baselineEntry = validHistory.find(entry => entry.at >= cutoff) || currentEntry;
        change30dPartialWindow = true;
      }

      change30d = currentEntry.value - baselineEntry.value;
    }
    
    if (history.length === 0) {
      // No history - check the ELO node for current rating
      console.log(`📊 No history for ${playerId}, checking ELO node...`);
      const eloRef = ref(database, `ELO/${playerId}`);
      const eloSnapshot = await get(eloRef);
      
      if (eloSnapshot.exists()) {
        const currentRating = eloSnapshot.val();
        console.log(`✅ Found existing ELO rating for ${playerId} in ELO node: ${currentRating}`);
        // This is their established rating, not a default
        return {
          current: currentRating,
          high: currentRating,
          low: currentRating,
          historyCount: 0,
          change30d: null,
          change30dPartialWindow: false,
          fromEloNode: true,
          isEstablished: true
        };
      }
      
      // No ELO found anywhere - truly new player, use default
      console.log(`🆕 New player ${playerId}, starting at default: ${SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING}`);
      const defaultRating = SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING;
      return {
        current: defaultRating,
        high: defaultRating,
        low: defaultRating,
        historyCount: 0,
        change30d: null,
        change30dPartialWindow: false,
        isDefault: true,
        isNewPlayer: true
      };
    }
    
    const eloValues = history.map(h => h.value);
    const currentElo = eloValues[eloValues.length - 1]; // Most recent from sorted history
    
    return {
      current: currentElo,
      high: Math.max(...eloValues),
      low: Math.min(...eloValues),
      historyCount: history.length,
      change30d,
      change30dPartialWindow,
      fromHistory: true
    };
  } catch (error) {
    console.error(`❌ Error getting ELO stats for ${playerId}:`, error);
    
    // Try to get from ELO node as fallback
    try {
      const eloRef = ref(database, `ELO/${playerId}`);
      const eloSnapshot = await get(eloRef);
      
      if (eloSnapshot.exists()) {
        const currentRating = eloSnapshot.val();
        console.log(`✅ Fallback: Found existing ELO rating for ${playerId} in ELO node: ${currentRating}`);
        return {
          current: currentRating,
          high: currentRating,
          low: currentRating,
          historyCount: 0,
          change30d: null,
          change30dPartialWindow: false,
          fromEloNode: true,
          isEstablished: true
        };
      }
    } catch (fallbackError) {
      console.error(`❌ Fallback error for ${playerId}:`, fallbackError);
    }
    
    // Only use default for truly new players
    console.log(`🆕 New player ${playerId}, starting at default: ${SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING}`);
    return {
      current: SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING,
      high: SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING,
      low: SOPHISTICATED_ELO_CONFIG.DEFAULT_RATING,
      historyCount: 0,
      change30d: null,
      change30dPartialWindow: false,
      isDefault: true,
      isNewPlayer: true
    };
  }
}
